import React, { useState } from 'react';
import { cn } from '@/utils/cn';
import SeoMeta from '@/components/seo/SeoMeta';
import { getServicePageServicesWithCta as getStaticServices, servicesPage, templateData } from '@/data/content';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '@/components/ui/Button';

// ─── Icon map ────────────────────────────────────────────────────────────────

const SVG_PROPS = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '1.8',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true as const,
    className: 'w-8 h-8',
};

const SERVICE_ICON_MAP: Record<string, React.ReactNode> = {
    '🎉': (
        <svg {...SVG_PROPS}>
            <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3z" />
        </svg>
    ),
    '💼': (
        <svg {...SVG_PROPS}>
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
    ),
    '🧺': (
        <svg {...SVG_PROPS}>
            <path d="M6 20h12M4 14h16M12 4a8 8 0 0 0-8 8h16a8 8 0 0 0-8-8z" />
        </svg>
    ),
    '🍽️': (
        <svg {...SVG_PROPS}>
            <path d="M3 2v6c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2M7 2v20" />
            <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h1v5a1 1 0 0 0 2 0V2" />
        </svg>
    ),
    '🛍️': (
        <svg {...SVG_PROPS}>
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
    ),
    '💡': (
        <svg {...SVG_PROPS}>
            <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
            <path d="M9 18h6" />
            <path d="M10 22h4" />
        </svg>
    ),
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface ServiceCta {
    label: string;
    to: string;
}

interface Service {
    icon: string;
    title: string;
    subtitle?: string;
    description: string;
    cta?: ServiceCta | null;
}

// ─── Animation variants ───────────────────────────────────────────────────────

const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
};

const FADE_DURATION = 0.6;

// ─── Sub-components ───────────────────────────────────────────────────────────

interface ServiceIconProps {
    icon: string;
}

const ServiceIcon: React.FC<ServiceIconProps> = ({ icon }) =>
    SERVICE_ICON_MAP[icon] ? (
        <>{SERVICE_ICON_MAP[icon]}</>
    ) : (
        <span className="text-3xl">{icon}</span>
    );

interface MobileServiceButtonProps {
    service: Service;
    index: number;
    isOpen: boolean;
    delay: number;
    onToggle: () => void;
}

const MobileServiceButton: React.FC<MobileServiceButtonProps> = ({
    service,
    isOpen,
    delay,
    onToggle,
}) => (
    <motion.button
        {...fadeInUp}
        transition={{ duration: FADE_DURATION, delay }}
        type="button"
        onClick={onToggle}
        className={cn(
            'flex flex-col items-center justify-center gap-3 p-5 rounded-3xl border-2 text-center transition-all duration-300',
            isOpen
                ? 'border-app-primary bg-app-primary/5 dark:bg-app-primary/10 shadow-lg shadow-app-primary/10'
                : 'border-app-border dark:border-gray-800 bg-white dark:bg-gray-800 hover:border-app-primary/40',
        )}
    >
        <span className={isOpen ? 'text-app-primary' : 'text-app-text dark:text-white'}>
            <ServiceIcon icon={service.icon} />
        </span>
        <span
            className={cn('text-sm font-bold leading-tight', isOpen ? 'text-app-primary' : 'text-app-text dark:text-white')}
        >
            {service.title}
        </span>
    </motion.button>
);

interface MobileExpandedPanelProps {
    service: Service;
}

const MobileExpandedPanel: React.FC<MobileExpandedPanelProps> = ({ service }) => (
    <AnimatePresence>
        <motion.div
            key={service.title}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="col-span-2 bg-orange-50 dark:bg-orange-900/10 border border-app-primary/20 rounded-3xl p-6 overflow-hidden mt-2"
        >
            {service.subtitle && (
                <p className="text-xs font-black text-app-primary uppercase tracking-widest mb-2">
                    {service.subtitle}
                </p>
            )}
            <p className="text-base text-app-muted dark:text-gray-300 leading-relaxed mb-4">
                {service.description}
            </p>
            {service.cta && (
                <Button to={service.cta.to} size="sm" variant="outline" className="px-6 py-2">
                    {service.cta.label} →
                </Button>
            )}
        </motion.div>
    </AnimatePresence>
);

interface DesktopServiceCardProps {
    service: Service;
    index: number;
}

const DesktopServiceCard: React.FC<DesktopServiceCardProps> = ({ service, index }) => (
    <motion.div
        {...fadeInUp}
        transition={{ duration: FADE_DURATION, delay: index * 0.1 }}
        className="flex flex-col w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.35rem)] bg-white dark:bg-gray-800 rounded-3xl border border-app-border dark:border-gray-700 p-8 shadow-sm hover:shadow-xl transition-all duration-500 group hover:-translate-y-2"
    >
        <div className="w-16 h-16 bg-app-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 text-app-primary">
            <ServiceIcon icon={service.icon} />
        </div>

        {service.subtitle && (
            <p className="text-xs font-black text-app-primary uppercase tracking-widest mb-2">
                {service.subtitle}
            </p>
        )}

        <h3 className="text-2xl font-black text-app-text dark:text-white mb-4 group-hover:text-app-primary transition-colors">
            {service.title}
        </h3>

        <p className="text-base text-app-muted dark:text-gray-400 leading-relaxed flex-1">
            {service.description}
        </p>

        {service.cta && (
            <div className="mt-8 pt-8 border-t border-app-border dark:border-gray-700">
                <Button to={service.cta.to} size="sm" variant="outline">
                    {service.cta.label} →
                </Button>
            </div>
        )}
    </motion.div>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Split a flat array into rows of `size` */
function chunkArray<T>(arr: T[], size: number): T[][] {
    return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
        arr.slice(i * size, i * size + size),
    );
}

const mapStaticService = (s: ReturnType<typeof getStaticServices>[number]): Service => ({
    icon: s.icon,
    title: s.title,
    subtitle: s.subtitle,
    description: s.description,
    cta: s.cta ?? null,
});

// ─── Page component ───────────────────────────────────────────────────────────

const Services: React.FC = () => {
    const services = getStaticServices().map(mapStaticService);
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const toggle = (index: number) =>
        setOpenIndex((prev) => (prev === index ? null : index));

    const mobileRows = chunkArray(services, 2);

    return (
        <>
            <SeoMeta page="services" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <div className="mb-6 flex justify-start">
                <Button to="/" size="sm" variant="outline" className="px-6 py-2">
                    ← {templateData.back_home}
                </Button>
            </div>

            {/* ── Mobile: accordion grid ── */}
            <div className="md:hidden">
                <motion.div
                    {...fadeInUp}
                    transition={{ duration: FADE_DURATION }}
                    className="bg-white dark:bg-gray-800 rounded-3xl border border-app-border dark:border-gray-700 p-8 shadow-sm mb-10"
                >
                    <h2 className="text-3xl font-black text-app-text dark:text-white mb-3">
                        {servicesPage.mobile_title}
                    </h2>
                    <p className="text-base text-app-muted dark:text-gray-400">
                        {servicesPage.mobile_subtitle}
                    </p>
                </motion.div>

                <div className="grid grid-cols-2 gap-4 mt-8">
                    {mobileRows.map((row, rowIndex) => {
                        const [serviceA, serviceB] = row;
                        const idxA = rowIndex * 2;
                        const idxB = idxA + 1;
                        const rowHasOpen =
                            openIndex === idxA || (serviceB !== undefined && openIndex === idxB);
                        const activeService = rowHasOpen ? services[openIndex!] : null;

                        return (
                            <React.Fragment key={serviceA.title}>
                                <MobileServiceButton
                                    service={serviceA}
                                    index={idxA}
                                    isOpen={openIndex === idxA}
                                    delay={0.1 + rowIndex * 0.1}
                                    onToggle={() => toggle(idxA)}
                                />

                                {serviceB !== undefined ? (
                                    <MobileServiceButton
                                        service={serviceB}
                                        index={idxB}
                                        isOpen={openIndex === idxB}
                                        delay={0.15 + rowIndex * 0.1}
                                        onToggle={() => toggle(idxB)}
                                    />
                                ) : (
                                    <div aria-hidden="true" />
                                )}

                                {rowHasOpen && activeService && (
                                    <MobileExpandedPanel service={activeService} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                <motion.div
                    {...fadeInUp}
                    transition={{ duration: FADE_DURATION, delay: 0.4 }}
                >
                    <Button to="/contact" fullWidth size="lg" className="mt-8">
                        {servicesPage.contact_button}
                    </Button>
                </motion.div>
            </div>

            {/* ── Desktop: card grid ── */}
            <div className="hidden md:block">
                <motion.div
                    {...fadeInUp}
                    transition={{ duration: FADE_DURATION }}
                    className="bg-white dark:bg-gray-800 rounded-3xl border border-app-border dark:border-gray-700 p-10 shadow-sm mb-12"
                >
                    <h1 className="text-5xl font-black text-app-text dark:text-white mb-4 tracking-tight">
                        {servicesPage.desktop_title}
                    </h1>
                    <p className="text-xl text-app-muted dark:text-gray-400 leading-relaxed">
                        {servicesPage.desktop_subtitle}
                    </p>
                </motion.div>

                <div className="flex flex-wrap justify-center gap-8">
                    {services.map((service, index) => (
                        <DesktopServiceCard key={service.title} service={service} index={index} />
                    ))}
                </div>

                <motion.div
                    {...fadeInUp}
                    transition={{ duration: FADE_DURATION, delay: 0.6 }}
                    className="mt-12 text-center"
                >
                    <Button to="/contact" size="lg" className="px-12">
                        {servicesPage.contact_button}
                    </Button>
                </motion.div>
            </div>
        </div>
        </>
    );
};

export default Services;
